// anteilFormat.ts — Regeln für Beteiligungsquoten an EINEM Ort.
//
// Welche Art Quote eine Rolle trägt, sagt die Matrix
// (LegalFormVerantwortlichkeit.quotenTyp, Migration 202608150004): Prozent beim
// GmbH-Gesellschafter, Euro beim Kommanditisten (§ 171 HGB), Bruchteil beim
// Wohnungs- oder Miteigentümer. Bis 08/2026 gab es das Anteilsfeld nur an der
// Rolle „Gesellschafter" — Kommanditistin, Aktionärin und Wohnungseigentümerin
// konnten ihre Quote gar nicht erfassen, obwohl die Matrix den Typ bis ins
// Frontend liefert.
//
// Gespeichert wird weiterhin ContactRelation.share (varchar(50), ungetypt). Der
// Text trägt seine Einheit deshalb SELBST mit, damit die sechs Anzeigestellen und
// das Organigramm ihn nicht falsch lesen können: „250/1000" ist kein Prozentwert,
// „25.000 €" keine Quote.
//
// Freitext bleibt Freitext: „ca. 25" muss durchgehen (PO 15.08.2026).
// Kanonisiert wird nur, was sauber parst.

import { formatGrouped, sanitizeGerman } from "./numberGrouping";

export type QuotenTyp = "PROZENT" | "EURO" | "BRUCHTEIL" | "KEINE";

const QUOTEN_TYPEN: readonly QuotenTyp[] = ["PROZENT", "EURO", "BRUCHTEIL", "KEINE"];

/** Unbekanntes (oder fehlendes) aus der Matrix gilt als „keine Quote". */
export function alsQuotenTyp(wert?: string | null): QuotenTyp {
  const t = (wert ?? "").trim().toUpperCase();
  return (QUOTEN_TYPEN as readonly string[]).includes(t) ? (t as QuotenTyp) : "KEINE";
}

/** ContactRelation.share ist varchar(50) (contactRelation.entity.ts). */
export const ANTEIL_MAX = 50;

export const ANTEIL_PLATZHALTER: Record<QuotenTyp, string> = {
  PROZENT: "z. B. 25 %",
  EURO: "z. B. 25.000 €",
  BRUCHTEIL: "z. B. 250/1000",
  KEINE: "",
};

export const ANTEIL_LABEL: Record<QuotenTyp, string> = {
  PROZENT: "Anteil",
  // Bewusst neutral: Haftsumme (§ 171 HGB, im Handelsregister eingetragen) und
  // Pflichteinlage (Innenverhältnis) sind verschiedene Beträge. Solange der PO
  // das nicht entschieden hat, wird keiner der beiden suggeriert.
  EURO: "Anteil / Einlage",
  BRUCHTEIL: "Miteigentumsanteil",
  KEINE: "Anteile",
};

export type AnteilAnalyse =
  | { art: "zahl"; typ: "PROZENT" | "EURO"; wert: number }
  | { art: "bruch"; zaehler: number; nenner: number }
  | { art: "freitext" };

/** Die selbst angehängte Einheit abstreifen — sonst wird aus „25 %" beim
 *  erneuten Blurren „25 % %". */
const ohneEinheit = (t: string) => t.replace(/\s*(%|€|EUR)\s*$/i, "").trim();

export function analysiereAnteil(roh: string, typ: QuotenTyp): AnteilAnalyse {
  const t = (roh ?? "").trim();
  if (!t) return { art: "freitext" };
  if (typ === "BRUCHTEIL") {
    const m = /^(\d+)\s*\/\s*(\d+)$/.exec(t);
    if (!m || Number(m[2]) === 0) return { art: "freitext" };
    return { art: "bruch", zaehler: Number(m[1]), nenner: Number(m[2]) };
  }
  if (typ === "PROZENT" || typ === "EURO") {
    const z = ohneEinheit(t);
    // Deutsche Schreibweise: Tausenderpunkt, Dezimalkomma.
    if (!/^\d{1,3}(\.\d{3})*(,\d+)?$|^\d+(,\d+)?$/.test(z)) return { art: "freitext" };
    const wert = Number(z.replace(/\./g, "").replace(",", "."));
    return Number.isFinite(wert) ? { art: "zahl", typ, wert } : { art: "freitext" };
  }
  return { art: "freitext" };
}

/** Kanonischer Speicher- und Anzeigetext — oder der Rohtext, wenn er nicht parst. */
export function formatiereAnteil(roh: string, typ: QuotenTyp): string {
  const a = analysiereAnteil(roh, typ);
  if (a.art === "bruch") return `${a.zaehler}/${a.nenner}`;
  if (a.art === "zahl") {
    // Gruppierung und Dezimalkomma über die vorhandenen Regeln (numberGrouping).
    const txt = formatGrouped(sanitizeGerman(String(a.wert).replace(".", ",")));
    return a.typ === "PROZENT" ? `${txt} %` : `${txt} €`;
  }
  return (roh ?? "").trim();
}

/** Weicher Hinweis — blockiert NICHTS (PO 15.08.2026). */
export function anteilHinweis(roh: string, typ: QuotenTyp): string | undefined {
  const a = analysiereAnteil(roh, typ);
  if (a.art === "zahl" && a.typ === "PROZENT" && a.wert > 100) return "Über 100 % — bitte prüfen.";
  if (a.art === "bruch" && a.zaehler > a.nenner) return "Zähler größer als Nenner — bitte prüfen.";
  if (a.art === "freitext" && (roh ?? "").trim()) return "Wird als Freitext gespeichert.";
  return undefined;
}
